import { Button as BaseButton } from "@base-ui/react/button";
import type { ComponentPropsWithRef, ReactNode } from "react";
import { cx } from "../presentation.tsx";
import { Icon, type IconName } from "../icons.tsx";
import { Spinner } from "./Spinner.tsx";

export type ButtonVariant = "primary" | "secondary" | "tonal" | "ghost" | "danger";
/** Relative size. The absolute size follows the presentation (32px console, 48px mobile, 80px kiosk). */
export type ButtonSize = "sm" | "md" | "lg";

export interface ButtonProps extends ComponentPropsWithRef<"button"> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  icon?: IconName;
  iconEnd?: IconName;
  /** Shows a spinner, sets aria-busy and blocks clicks while keeping focus. */
  loading?: boolean;
  fullWidth?: boolean;
  children?: ReactNode;
}

export function Button({
  variant = "primary", size = "md", icon, iconEnd, loading = false, fullWidth = false,
  className, children, disabled, type = "button", ...rest
}: ButtonProps) {
  return (
    <BaseButton
      {...rest}
      type={type}
      disabled={disabled || loading}
      focusableWhenDisabled={loading}
      aria-busy={loading || undefined}
      className={cx("pmu-button", `pmu-button--${variant}`, size !== "md" && `pmu-button--${size}`, fullWidth && "pmu-button--full", className)}
    >
      {loading ? <Spinner size="sm" label="" /> : icon && <Icon name={icon} />}
      {children}
      {iconEnd && !loading && <Icon name={iconEnd} />}
    </BaseButton>
  );
}

export interface IconButtonProps extends Omit<ButtonProps, "icon" | "iconEnd" | "children" | "fullWidth"> {
  icon: IconName;
  /** Required accessible name (also the tooltip). */
  label: string;
}

export function IconButton({ icon, label, variant = "ghost", className, title, ...rest }: IconButtonProps) {
  return (
    <Button {...rest} variant={variant} aria-label={label} title={title ?? label} className={cx("pmu-icon-button", className)}>
      <Icon name={icon} />
    </Button>
  );
}
